import React, { useState } from 'react';
import { Beer } from 'lucide-react';
import { SEO } from '../components/SEO';

interface AlcoholState {
  gender: 'M' | 'F';
  weight: string;
  drinks: {
    type: 'beer' | 'wine' | 'spirits';
    volume: string;
    percentage: string;
    count: string;
  }[];
  timePassed: string;
}

function KalkulatorAlkohola() {
  const [values, setValues] = useState<AlcoholState>({
    gender: 'M',
    weight: '',
    drinks: [{ type: 'beer', volume: '500', percentage: '5', count: '1' }],
    timePassed: '0'
  });

  const [result, setResult] = useState<{
    bac: number;
    soberTime: number;
    status: string;
  } | null>(null);

  const alcoholContent = {
    beer: { defaultVolume: 500, defaultPercentage: 5 },
    wine: { defaultVolume: 200, defaultPercentage: 12 },
    spirits: { defaultVolume: 50, defaultPercentage: 40 }
  };

  const calculateBAC = () => {
    const weight = parseFloat(values.weight);
    const hours = parseFloat(values.timePassed);
    
    if (isNaN(weight) || isNaN(hours)) return null;

    // Calculate total alcohol consumed in grams
    const totalAlcohol = values.drinks.reduce((acc, drink) => {
      const volume = parseFloat(drink.volume) || 0;
      const percentage = parseFloat(drink.percentage) || 0;
      const count = parseFloat(drink.count) || 0;
      return acc + (volume * (percentage / 100) * 0.789 * count);
    }, 0);

    // Widmark formula
    const r = values.gender === 'M' ? 0.68 : 0.55; // Body water constant
    let bac = (totalAlcohol / (weight * 1000 * r)) * 100;
    
    // Subtract alcohol eliminated over time (0.015 per hour)
    bac -= 0.015 * hours;
    bac = Math.max(0, bac);

    // Calculate time until sober (BAC < 0.02)
    const soberTime = Math.max(0, ((bac - 0.02) / 0.015));

    let status = '';
    if (bac === 0) status = 'Trijezno stanje';
    else if (bac < 0.02) status = 'Minimalna količina alkohola';
    else if (bac < 0.05) status = 'Blago alkoholizirano';
    else if (bac < 0.08) status = 'Pripito stanje';
    else if (bac < 0.15) status = 'Pijano stanje';
    else if (bac < 0.25) status = 'Teško pijano stanje';
    else status = 'Izuzetno opasno stanje';

    return {
      bac: parseFloat(bac.toFixed(3)),
      soberTime: parseFloat(soberTime.toFixed(1)),
      status
    };
  };

  const addDrink = () => {
    setValues(prev => ({
      ...prev,
      drinks: [...prev.drinks, { type: 'beer', volume: '500', percentage: '5', count: '1' }]
    }));
  };

  const removeDrink = (index: number) => {
    setValues(prev => ({
      ...prev,
      drinks: prev.drinks.filter((_, i) => i !== index)
    }));
  };

  const updateDrink = (index: number, field: keyof typeof values.drinks[0], value: string) => {
    setValues(prev => ({
      ...prev,
      drinks: prev.drinks.map((drink, i) => {
        if (i === index) {
          if (field === 'type') {
            const defaults = alcoholContent[value as keyof typeof alcoholContent];
            return {
              ...drink,
              [field]: value,
              volume: defaults.defaultVolume.toString(),
              percentage: defaults.defaultPercentage.toString()
            };
          }
          return { ...drink, [field]: value };
        }
        return drink;
      })
    }));
  };

  const handleCalculate = () => {
    const results = calculateBAC();
    setResult(results);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Alkohola",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte razinu alkohola u krvi i vrijeme do trijeznosti. Precizni izračuni bazirani na Widmarkovoj formuli.",
    "url": "https://online-kalkulator.com/kalkulator-alkohola",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Alkometar - Kalkulator Alkohola | Izračun Alkohola u Krvi"
        description="Izračunajte razinu alkohola u krvi i vrijeme do trijeznosti. Precizni izračuni bazirani na Widmarkovoj formuli."
        canonicalUrl="/kalkulator-alkohola"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Beer className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Alkometar - Kalkulator Alkohola u Krvi</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol
                  </label>
                  <select
                    value={values.gender}
                    onChange={(e) => setValues(prev => ({ ...prev, gender: e.target.value as 'M' | 'F' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="M">Muško</option>
                    <option value="F">Žensko</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Težina (kg)
                  </label>
                  <input
                    type="number"
                    value={values.weight}
                    onChange={(e) => setValues(prev => ({ ...prev, weight: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 75"
                  />
                </div>
              </div>

              {/* Drinks */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-700">Konzumirano piće</h3>
                  <button
                    onClick={addDrink}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  >
                    + Dodaj piće
                  </button>
                </div>

                {values.drinks.map((drink, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vrsta
                      </label>
                      <select
                        value={drink.type}
                        onChange={(e) => updateDrink(index, 'type', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="beer">Pivo</option>
                        <option value="wine">Vino</option>
                        <option value="spirits">Žestoko</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Volumen (ml)
                      </label>
                      <input
                        type="number"
                        value={drink.volume}
                        onChange={(e) => updateDrink(index, 'volume', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alkohol (%)
                      </label>
                      <input
                        type="number"
                        value={drink.percentage}
                        onChange={(e) => updateDrink(index, 'percentage', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={drink.count}
                        onChange={(e) => updateDrink(index, 'count', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                      />
                      {values.drinks.length > 1 && (
                        <button
                          onClick={() => removeDrink(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded transition"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vrijeme od početka konzumacije (sati)
                </label>
                <input
                  type="number"
                  value={values.timePassed}
                  onChange={(e) => setValues(prev => ({ ...prev, timePassed: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="npr. 2"
                  min="0"
                  step="0.5"
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {result.bac.toFixed(3)}‰
                    </p>
                    <p className="text-lg text-gray-700">
                      {result.status}
                    </p>
                  </div>

                  {result.soberTime > 0 && (
                    <div className="text-center text-gray-600">
                      Procijenjeno vrijeme do trijeznosti: {result.soberTime} sati
                    </div>
                  )}

                  <div className="mt-4">
                    <div className="h-4 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white opacity-50"
                        style={{ 
                          width: `${Math.min(100, (1 - result.bac / 0.3) * 100)}%`,
                          transition: 'width 0.5s ease-in-out'
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>0.00‰</span>
                      <span>0.15‰</span>
                      <span>0.30‰</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Alkohola</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator alkohola koristi Widmarkovu formulu za procjenu koncentracije alkohola u
                  krvi (BAC). Uzima u obzir vašu težinu, spol, količinu i vrstu konzumiranog alkohola,
                  te proteklo vrijeme od početka konzumacije.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Razine Alkohola u Krvi</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">0.00 - 0.02‰</span>
                    <span>Trijezno stanje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">0.02 - 0.05‰</span>
                    <span>Blago alkoholizirano</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="font-medium">0.05 - 0.08‰</span>
                    <span>Pripito stanje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="font-medium">0.08 - 0.15‰</span>
                    <span>Pijano stanje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">0.15 - 0.25‰</span>
                    <span>Teško pijano stanje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">&gt; 0.25‰</span>
                    <span>Izuzetno opasno stanje</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite svoju težinu i odaberite spol</li>
                  <li>Dodajte konzumirana pića s njihovim karakteristikama</li>
                  <li>Unesite vrijeme proteklo od početka konzumacije</li>
                  <li>Kliknite "Izračunaj" za rezultate</li>
                  <li>Pregledajte procijenjenu razinu alkohola i vrijeme do trijeznosti</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Rezultati su približni i informativne prirode</li>
                  <li>Ne koristite za donošenje odluka o vožnji</li>
                  <li>Razni faktori mogu utjecati na stvarnu razinu alkohola</li>
                  <li>Konzultirajte se s liječnikom za točne vrijednosti</li>
                  <li>Uvijek imajte na umu da je vožnja pod utjecajem alkohola opasna i nezakonita</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori koji Utječu na BAC</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Težina i sastav tijela</li>
                  <li>Spol (različita apsorpcija kod muškaraca i žena)</li>
                  <li>Brzina konzumacije</li>
                  <li>Hrana u želucu</li>
                  <li>Individualni metabolizam</li>
                  <li>Umor i stres</li>
                  <li>Lijekovi i zdravstveno stanje</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorAlkohola;